import { fetchWithRequestId } from './fetch-with-request-id.js';

/**
 * Error thrown by {@link apiFetch} on any non-2xx response. Carries the
 * HTTP status and the parsed JSON error payload (when the server sent one)
 * so callers can branch on `error.payload?.error` codes.
 */
export class ApiError extends Error {
  /**
   * @param {number} status
   * @param {object|null} payload
   */
  constructor(status, payload) {
    super(payload?.error ? `${status} ${payload.error}` : `HTTP ${status}`);
    this.name = 'ApiError';
    this.status = status;
    this.payload = payload;
  }
}

/**
 * JSON fetch helper for the platform API (same origin, cookie session).
 *
 * - stamps `x-request-id` (via fetchWithRequestId)
 * - serializes a plain-object `body` as JSON ; a raw `Blob`/`File`/
 *   `ReadableStream`/`FormData` body is passed THROUGH untouched so the browser
 *   sets `Content-Type` (from the File) + `Content-Length` itself — required by
 *   the M1 attachments upload (raw streamed body, never multipart). (M3 Pass-3.)
 * - 401 → hard redirect to /login (session expired)
 * - non-2xx → throws {@link ApiError} with the parsed payload
 *
 * @param {string} path e.g. `/api/offices/${officeId}/summary`
 * @param {{ method?: string, body?: object|Blob|ReadableStream|FormData, signal?: AbortSignal, text?: boolean, withHeaders?: boolean }} [options]
 *   `text: true` returns the raw response text instead of parsed JSON (run logs, S-006).
 *   `withHeaders: true` returns `{ body, headers }` (the response Headers) — the run Prompt view reads its
 *   verification headers (run-prompt-compiler S-010).
 * @returns {Promise<any>} parsed JSON body (null for 204), or a string with `text: true`
 */
function isRawBody(body) {
  return (
    body instanceof Blob || // covers File (a Blob subclass)
    body instanceof FormData ||
    body instanceof ArrayBuffer ||
    body instanceof URLSearchParams ||
    (typeof ReadableStream !== 'undefined' && body instanceof ReadableStream)
  );
}

// A page mount can fire many queries at once; if the session is gone they ALL
// 401. Redirect to /login only once (and never when already there) so a burst
// of 401s doesn't spam window.location.assign or fight an in-flight navigation.
let loginRedirectPending = false;
function redirectToLoginOnce() {
  if (typeof window === 'undefined') return;
  if (loginRedirectPending) return;
  if (window.location.pathname === '/login') return;
  loginRedirectPending = true;
  window.location.assign('/login');
}

export async function apiFetch(path, {
  method = 'GET', body, signal, text = false, withHeaders = false,
} = {}) {
  const raw = body !== undefined && isRawBody(body);
  const response = await fetchWithRequestId(path, {
    method,
    credentials: 'include',
    signal,
    // Only stamp JSON content-type for a plain object — let the browser set it
    // for a raw streamed body (File/Blob → its own mime + length).
    headers: body !== undefined && !raw ? { 'content-type': 'application/json' } : undefined,
    body: body === undefined ? undefined : raw ? body : JSON.stringify(body),
  });

  if (response.status === 401) {
    redirectToLoginOnce();
    throw new ApiError(401, { error: 'unauthenticated' });
  }

  if (!response.ok) {
    let payload = null;
    try {
      payload = await response.json();
    } catch {
      // non-JSON error body — keep payload null
    }
    throw new ApiError(response.status, payload);
  }

  const parsed = response.status === 204 ? (text ? '' : null) : await (text ? response.text() : response.json());
  return withHeaders ? { body: parsed, headers: response.headers } : parsed;
}
